/**
 * StandIdentityFields.jsx — standın künyesi: salon + stand kodu + stand adı (tabelaAdi)
 *
 * Backlog #208 (2026-10-10): serbest metin alanın etiketi "Tabela Adı" değil "Stand Adı"; listelerde
 * standın adı olarak bu görünüyor. Veri alanı aynı (tabelaAdi, VARCHAR 255), API değişmedi. Holden
 * kurulan kod ("1.A10", standName) karışmasın diye artık "Stand No" diye anılıyor.
 *
 * Stand Yarat sayfası (pages/Management/Projects/StandYaratPage.jsx) ve Stand
 * Listesi'ndeki "Stand Düzenle" popup'ının ORTAK üst bloğu. İki yerde birebir aynı
 * görünmesi ve aynı adlandırma kuralını uygulaması için tek bileşen.
 *
 * ─── NEDEN "HOL NO" KUTUSU YOK (⚠️ 2026-09-03'te DEĞİŞTİ) ───────────────────────
 * Ad kuralının tamamı services/standNaming.js'te; özeti: adın hol yarısı HER ZAMAN
 * `salon.salon_name`'den gelir, gönderdiğimiz bir metinden değil. Bu yüzden eski
 * "Hol No" SERBEST METİN kutusu kaldırıldı — salonu seçmek hol adını da seçmektir.
 *
 * Kutunun kaldırılma sebebi teorik değil: stand 57557 salon 2'de otururken "1A19"
 * adıyla kaydedilmişti, çünkü hol yarısı elle yazılabiliyordu ve iki değeri
 * karşılaştıran hiçbir şey yoktu.
 *
 * Kullanıcıya artık SADECE `standCode` (holün İÇİNDEKİ kod, örn. "A10") soruluyor;
 * tam adı sunucu kuruyor. Aşağıdaki "Tam stand adı" satırı bir ÖN İZLEMEDİR —
 * yazma sonrası gerçek ad sunucunun döndürdüğü `standName`'dir.
 *
 * ⚠️ Salon adı her zaman numara değil: 329 stand "ANA FUAYE" adlı holde duruyor ve
 * adları "ANA FUAYE.A10" oluyor. Tuhaflık dahil, hol kendi adını dayatır.
 */
import React from "react";
import { Row, Col, FormGroup, Label, Input } from "reactstrap";
import { composeStandName } from "../../services/standNaming";
import { useTranslation } from "react-i18next";

// tabelaAdi'yi tutan kolon VARCHAR(255) ve backend hiçbir uzunluk kontrolü yapmıyor
// (aşarsa MySQL 500 veriyor) — sınır burada.
export const TABELA_ADI_MAX = 255;

/**
 * @param {object}   form              { salonId, standCode, tabelaAdi }
 * @param {function} update            (patch) => void
 * @param {array}    salonOptions      [{ salonId, salonName }]
 * @param {boolean}  salonsUnavailable salon listesi çekilemedi → elle salonId girişi
 * @param {boolean}  disabled
 * @param {boolean}  codeInvalid       #547: sunucu bu holde aynı adlı stand var diye reddetti → Stand Kodu
 *                                     kutusu kırmızı; metni çağıran kendi uyarısında gösterir
 * @param {node}     tabelaHint        Stand Adı (tabelaAdi) altındaki açıklamanın yerine geçer
 * @param {boolean}  compact           Üç alan tek satırda, açıklamalar ve "Tam stand adı"
 *                                     satırı yok — Stand Yarat bu modu kullanıyor ve ad ön
 *                                     izlemesini kendi alt çubuğunda gösteriyor.
 */
export default function StandIdentityFields({
  form, update, salonOptions, salonsUnavailable, disabled = false, tabelaHint = null, compact = false, codeInvalid = false,
}) {
  const { t } = useTranslation("components");
  const secilenSalon = salonOptions.find((s) => String(s.salonId) === String(form.salonId));
  const salonName = secilenSalon?.salonName || "";
  const fullName = composeStandName(salonName, form.standCode);

  if (compact) {
    return (
      <Row className="g-3">
        <Col md={4}>
          <Label size="sm" className="pt-0">{t("standIdentityFields.salon")}{" "}<span className="text-danger">*</span></Label>
          {salonsUnavailable ? (
            <Input
              type="number" bsSize="sm" value={form.salonId} disabled={disabled}
              placeholder={t("standIdentityFields.salonIdListeAlinamadi")}
              onChange={(e) => update({ salonId: e.target.value })}
            />
          ) : (
            <Input
              type="select" bsSize="sm" value={form.salonId} disabled={disabled}
              onChange={(e) => update({ salonId: e.target.value })}
            >
              <option value="">{t("standIdentityFields.seciniz")}</option>
              {salonOptions.map((s) => (
                <option key={s.salonId} value={s.salonId}>{s.salonName}</option>
              ))}
            </Input>
          )}
        </Col>
        <Col md={3}>
          <Label size="sm" className="pt-0" title={t("standIdentityFields.holunIcindekiKodHolAdi")}>
            {t("standIdentityFields.standKodu")}{" "}<span className="text-danger">*</span>
          </Label>
          <Input
            bsSize="sm" value={form.standCode} placeholder="A10" disabled={disabled} invalid={codeInvalid}
            onChange={(e) => update({ standCode: e.target.value })}
          />
        </Col>
        <Col md={5}>
          {/* WAS (#208 öncesi): <Label size="sm" className="pt-0">Tabela Adı</Label> */}
          <Label size="sm" className="pt-0">{t("standIdentityFields.standAdi")}</Label>
          <Input
            bsSize="sm" value={form.tabelaAdi} maxLength={TABELA_ADI_MAX} disabled={disabled}
            placeholder={t("standIdentityFields.firmaKisaAdindanOnerilir")}
            onChange={(e) => update({ tabelaAdi: e.target.value, tabelaAdiTouched: true })}
          />
          {tabelaHint && <div className="text-muted small mt-1">{tabelaHint}</div>}
        </Col>
      </Row>
    );
  }

  return (
    <Row className="g-3">
      <Col md={5}>
        <FormGroup className="mb-0">
          <Label size="sm">{t("standIdentityFields.salon")}{" "}<span className="text-danger">*</span></Label>
          {salonsUnavailable ? (
            <>
              <Input
                type="number" bsSize="sm" value={form.salonId} disabled={disabled}
                onChange={(e) => update({ salonId: e.target.value })}
              />
              <div className="text-muted small mt-1">{t("standIdentityFields.salonListesiAlinamadiSalonId")}</div>
            </>
          ) : (
            <>
              <Input
                type="select" bsSize="sm" value={form.salonId} disabled={disabled}
                onChange={(e) => update({ salonId: e.target.value })}
              >
                <option value="">{t("standIdentityFields.seciniz")}</option>
                {salonOptions.map((s) => (
                  <option key={s.salonId} value={s.salonId}>
                    {`${s.salonName} (#${s.salonId})`}
                  </option>
                ))}
              </Input>
              <div className="text-muted small mt-1">
                {t("standIdentityFields.standAdininHolYarisiBu")}
              </div>
            </>
          )}
        </FormGroup>
      </Col>
      <Col md={7}>
        <FormGroup className="mb-0">
          <Label size="sm">{t("standIdentityFields.standKodu")}{" "}<span className="text-danger">*</span></Label>
          <Input
            bsSize="sm" value={form.standCode} placeholder="A10" disabled={disabled} invalid={codeInvalid}
            onChange={(e) => update({ standCode: e.target.value })}
          />
          <div className="text-muted small mt-1">
            {t("standIdentityFields.standinHolIcindekiKoduHol")}
          </div>
        </FormGroup>
      </Col>
      <Col md={12}>
        <div className="small">
          {/* WAS (#208 öncesi): Tam stand adı: */}
          <span className="text-muted">{t("standIdentityFields.tamStandNo")}{" "}</span>
          <span className="fw-semibold">{fullName || <span className="text-muted">—</span>}</span>
          {fullName && (
            <span className="text-muted ms-2">
              {salonName
                ? t("standIdentityFields.salonAdiKodSunucudaKurulur")
                // Salon listesi yoksa hol adını BİLMİYORUZ; burada yalnız kodu
                // gösterip tam adı uydurmuyoruz — sunucu salonun adından kuracak.
                : t("standIdentityFields.holAdiSalondanGelecekOn")}
            </span>
          )}
        </div>
      </Col>
      <Col md={12}>
        <FormGroup className="mb-0">
          {/* WAS (#208 öncesi): Tabela Adı <span className="text-muted fw-normal">(opsiyonel)</span> */}
          <Label size="sm">
            {t("standIdentityFields.standAdi")}{" "}<span className="text-muted fw-normal">{t("standIdentityFields.opsiyonel")}</span>
          </Label>
          <Input
            bsSize="sm" value={form.tabelaAdi} maxLength={TABELA_ADI_MAX} disabled={disabled}
            placeholder="Fanta"
            onChange={(e) => update({ tabelaAdi: e.target.value, tabelaAdiTouched: true })}
          />
          <div className="text-muted small mt-1">
            {tabelaHint || (
              <>
                {/* WAS (#208 öncesi): Standın üzerindeki tabelada yazan ad. Firma ünvanından da kısa
                    adından da farklı olabilir (Coca Cola Corporation → Coca Cola → Fanta). */}
                {t("standIdentityFields.standListelerindeStandinAdiOlarak")}
              </>
            )}
          </div>
        </FormGroup>
      </Col>
    </Row>
  );
}
